import type { ReactNode, KeyboardEvent } from "react";
import { cn } from "@/cn";
import type { BadgeConfig, MetadataItem } from "./types";

export interface EntityCompactItemProps {
  id: string;
  title: string | undefined;
  description?: string | ReactNode;
  badges?: BadgeConfig[];
  icon?: ReactNode;
  metadata?: MetadataItem[];
  actions?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function EntityCompactItem({
  title,
  description,
  badges = [],
  icon,
  metadata,
  actions,
  onClick,
  className = "",
}: EntityCompactItemProps) {
  const isInteractive = Boolean(onClick);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `Open ${title || "Untitled"}` : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "group rounded-lg border border-border bg-card p-3",
        isInteractive &&
          "cursor-pointer transition hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {icon}
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-foreground">
                {title || "Untitled"}
              </h3>
              {badges.map((badge, idx) => (
                <span
                  key={idx}
                  className={cn(
                    "rounded px-2 py-0.5 text-xs",
                    badge.colorClass,
                  )}
                >
                  {badge.label}
                </span>
              ))}
            </div>
            {description ? (
              <div className="text-xs text-muted-foreground">
                {typeof description === "string" ? (
                  <p className="line-clamp-1">{description}</p>
                ) : (
                  description
                )}
              </div>
            ) : null}
            {metadata && metadata.length > 0 ? (
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {metadata.map((item, idx) => (
                  <div
                    key={idx}
                    className={cn("flex items-center gap-1", item.className)}
                  >
                    {item.icon}
                    {item.label ? <span>{item.label}:</span> : null}
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
